package com.partejar.app.core.ui.components

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.RowScope
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.interaction.collectIsPressedAsState
import androidx.compose.material3.Icon
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import com.partejar.app.core.ui.theme.PartejarIcons
import androidx.compose.foundation.layout.size
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.LocalContentColor
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Shape
import androidx.compose.ui.semantics.clearAndSetSemantics
import androidx.compose.ui.semantics.contentDescription
import androidx.compose.ui.semantics.semantics
import androidx.compose.ui.semantics.stateDescription
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.partejar.app.core.ui.theme.PartejarTheme

enum class PartejarButtonVariant {
    PRIMARY,
    SECONDARY,
    TERTIARY,
    DESTRUCTIVE,
    DESTRUCTIVE_SECONDARY
}

enum class PartejarButtonSize {
    DEFAULT,
    COMPACT
}

@Composable
fun PartejarButton(
    text: String,
    onClick: () -> Unit,
    modifier: Modifier = Modifier,
    isLoading: Boolean = false,
    enabled: Boolean = true,
    variant: PartejarButtonVariant = PartejarButtonVariant.PRIMARY,
    leadingIcon: (@Composable RowScope.() -> Unit)? = null,
    loadingDescription: String = "Carregando",
    motionEnabled: Boolean = false,
    size: PartejarButtonSize = PartejarButtonSize.DEFAULT,
    fillWidth: Boolean = true,
    shape: Shape = MaterialTheme.shapes.large
) {
    val dimensions = PartejarTheme.dimensions
    val spacing = PartejarTheme.spacing
    val interactionSource = remember { MutableInteractionSource() }
    val pressed by interactionSource.collectIsPressedAsState()
    val motionModifier = if (motionEnabled) Modifier.partejarPressFeedback(pressed, enabled || isLoading) else Modifier

    val buttonModifier = modifier
        .then(if (fillWidth) Modifier.fillMaxWidth() else Modifier)
        .heightIn(min = if (size == PartejarButtonSize.DEFAULT) 56.dp else dimensions.minimumTouchTarget)
        .then(motionModifier)
        .semantics {
            if (isLoading) {
                contentDescription = text
                stateDescription = loadingDescription
            }
        }

    val buttonEnabled = enabled && !isLoading

    val content: @Composable RowScope.() -> Unit = {
        PartejarButtonContent(
            text = text,
            isLoading = isLoading,
            leadingIcon = leadingIcon,
            loadingText = if (motionEnabled) loadingDescription else null,
            size = size
        )
    }

    val contentPadding = if (size == PartejarButtonSize.COMPACT) {
        PaddingValues(horizontal = spacing.space16)
    } else {
        PaddingValues(0.dp)
    }

    when (variant) {
        PartejarButtonVariant.PRIMARY,
        PartejarButtonVariant.DESTRUCTIVE -> Button(
            onClick = onClick,
            modifier = buttonModifier,
            enabled = buttonEnabled,
            interactionSource = interactionSource,
            shape = shape,
            colors = filledButtonColors(variant),
            contentPadding = contentPadding,
            elevation = ButtonDefaults.buttonElevation(
                defaultElevation = 0.dp,
                pressedElevation = 0.dp,
                disabledElevation = 0.dp
            ),
            content = content
        )

        PartejarButtonVariant.SECONDARY,
        PartejarButtonVariant.DESTRUCTIVE_SECONDARY -> OutlinedButton(
            onClick = onClick,
            modifier = buttonModifier,
            enabled = buttonEnabled,
            interactionSource = interactionSource,
            shape = shape,
            colors = ButtonDefaults.outlinedButtonColors(
                contentColor = if (variant == PartejarButtonVariant.DESTRUCTIVE_SECONDARY) {
                    MaterialTheme.colorScheme.error
                } else {
                    MaterialTheme.colorScheme.primary
                },
                containerColor = MaterialTheme.colorScheme.surfaceContainerLow,
                disabledContentColor = MaterialTheme.colorScheme.onSurfaceVariant,
                disabledContainerColor = MaterialTheme.colorScheme.surfaceContainer
            ),
            border = BorderStroke(
                width = dimensions.strokeThin,
                color = if (buttonEnabled) {
                    if (variant == PartejarButtonVariant.DESTRUCTIVE_SECONDARY) {
                        MaterialTheme.colorScheme.error
                    } else {
                        MaterialTheme.colorScheme.outline.copy(alpha = 0.78f)
                    }
                } else {
                    MaterialTheme.colorScheme.outlineVariant
                }
            ),
            contentPadding = contentPadding,
            content = content
        )

        PartejarButtonVariant.TERTIARY -> TextButton(
            onClick = onClick,
            modifier = buttonModifier,
            enabled = buttonEnabled,
            interactionSource = interactionSource,
            shape = shape,
            colors = ButtonDefaults.textButtonColors(
                contentColor = MaterialTheme.colorScheme.primary,
                disabledContentColor = MaterialTheme.colorScheme.outline
            ),
            contentPadding = contentPadding,
            content = content
        )
    }
}

@Composable
private fun filledButtonColors(variant: PartejarButtonVariant) =
    if (variant == PartejarButtonVariant.DESTRUCTIVE) {
        ButtonDefaults.buttonColors(
            containerColor = MaterialTheme.colorScheme.error,
            contentColor = MaterialTheme.colorScheme.onError,
            disabledContainerColor = MaterialTheme.colorScheme.surfaceContainerHigh,
            disabledContentColor = MaterialTheme.colorScheme.onSurfaceVariant
        )
    } else {
        ButtonDefaults.buttonColors(
            containerColor = MaterialTheme.colorScheme.primary,
            contentColor = MaterialTheme.colorScheme.onPrimary,
            disabledContainerColor = MaterialTheme.colorScheme.surfaceContainerHigh,
            disabledContentColor = MaterialTheme.colorScheme.onSurfaceVariant
        )
    }

@Composable
private fun RowScope.PartejarButtonContent(
    text: String,
    isLoading: Boolean,
    leadingIcon: (@Composable RowScope.() -> Unit)?,
    loadingText: String?,
    size: PartejarButtonSize
) {
    val spacing = PartejarTheme.spacing
    val dimensions = PartejarTheme.dimensions

    if (isLoading && loadingText != null) {
        androidx.compose.foundation.layout.Row(
            horizontalArrangement = Arrangement.spacedBy(spacing.space8),
            verticalAlignment = Alignment.CenterVertically
        ) {
            if (reducedMotion()) Icon(PartejarIcons.Clinical.schedule, null, Modifier.size(dimensions.iconMedium))
            else CircularProgressIndicator(Modifier.size(dimensions.iconMedium).clearAndSetSemantics { }, color = LocalContentColor.current, strokeWidth = dimensions.strokeStrong)
            Text(loadingText, style = MaterialTheme.typography.labelLarge)
        }
    } else if (isLoading) {
        CircularProgressIndicator(
            modifier = Modifier
                .size(dimensions.iconMedium)
                .clearAndSetSemantics { },
            color = LocalContentColor.current,
            strokeWidth = 3.dp
        )
    } else {
        androidx.compose.foundation.layout.Row(
            horizontalArrangement = Arrangement.spacedBy(spacing.space8),
            verticalAlignment = Alignment.CenterVertically
        ) {
            leadingIcon?.invoke(this)

            if (size == PartejarButtonSize.COMPACT) {
                Text(text = text, style = MaterialTheme.typography.labelLarge, fontWeight = FontWeight.SemiBold)
            } else {
                Text(
                    text = text,
                    fontSize = 15.sp,
                    fontWeight = FontWeight.SemiBold,
                    letterSpacing = 0.3.sp
                )
            }
        }
    }
}
